/* eslint-disable */
import {
  useEffect,
  useState,
  useMemo,
  useCallback,
  lazy,
  Suspense,
} from "react";

import classNames from "classnames";
import { Alert } from "@/components/alert/alert";
import { EnvironmentalEffects } from "@/components/environment/environmental-effects";
import { NightOverlay } from "@/components/environment/lighting-effects";
import { PokemonCampfireDisplay } from "@/components/pokemon/pokemon-campfire-display";
import { Button } from "@/components/ui/buttons/button";
import { ErrorBoundary } from "@/components/error-boundary";
import { RouteLoadingFallback } from "@/components/ui/overlays/route-loading-fallback";
import { AnimatedVFX } from "@/components/vfx/animated-vfx";
import { useDuelQueue } from "@/hooks/use-duel-queue";
import { BackIcon } from "@/icons";
import { useAuth } from "@/providers/auth-provider";
import { useSPAContext } from "@/providers/spa-provider";
import { useUser } from "@/providers/user-provider";
import { listInventoryPokemons } from "@/services/inventory/list-inventory-pokemons";
import { RouteNames } from "@/types/route-names";
import { ArenaTeamSelectView } from "@/views/arena/arena-team-select-view";
import { DexHomeView } from "@/views/dex/dex-home-view";
import { DexSetDetailsView } from "@/views/dex/dex-set-details-view";
import { HomeBottomNav } from "@/views/home/home-bottom-nav";import { HomeHeader } from "@/views/home/home-header";
import { MotionItem } from "@/motion/motion-primitives";
import { InventoryPokeView } from "@/views/inventory-poke/inventory-poke-view";
import { DeckEditView } from "@/views/item-decks/deck-edit-view";
import { DeckListView } from "@/views/item-decks/deck-list-view";
import { LeaderboardView } from "@/views/leaderboard/leaderboard-view";
import { PacksView } from "@/views/packs/packs-view";
import { PlayMenuView } from "@/views/play-menu/play-menu-view";
import { PokemonInfoView } from "@/views/pokemon-info/pokemon-info-view";
import { PlayerProfileView } from "@/views/profile/player-profile-view";
import { ProfileSettingsView } from "@/views/profile/profile-settings-view";
import { FriendsView } from "@/views/social/friends-view";
import { HomeFeedTicker } from "@/views/social/home-feed-ticker";
import { resolveDevView, isDevRoute } from "@/routes/dev/dev-router";
import type { WeatherType } from "@/components/environment/environmental-effects";

// Pixi-heavy / rarely-visited routes are code-split out of the main bundle.
const Battle = lazy(() => import("@/routes/Battle"));
const Duel = lazy(() =>
  import("@/routes/Duel").then((m) => ({ default: m.Duel })),
);
const DojoView = lazy(() =>
  import("@/views/dojo/dojo-view").then((m) => ({ default: m.DojoView })),
);
const DojoTrainingConfigView = lazy(() =>
  import("@/views/dojo/dojo-training-config-view").then((m) => ({
    default: m.DojoTrainingConfigView,
  })),
);
const DojoTeamSelectView = lazy(() =>
  import("@/views/dojo/dojo-team-select-view").then((m) => ({
    default: m.DojoTeamSelectView,
  })),
);
const DojoSandboxView = lazy(() =>
  import("@/views/dojo/dojo-sandbox-view").then((m) => ({
    default: m.DojoSandboxView,
  })),
);
const ShopView = lazy(() =>
  import("@/views/shop/shop-view").then((m) => ({ default: m.ShopView })),
);
const TrainingFacilityView = lazy(() =>
  import("@/views/training-facility/dojo-training-view").then((m) => ({
    default: m.DojoTrainingView,
  })),
);
const SiegeView = lazy(() =>
  import("@/views/siege/siege-view").then((m) => ({ default: m.SiegeView })),
);
const ChallengeView = lazy(() =>
  import("@/views/challenge/challenge-view").then((m) => ({ default: m.ChallengeView })),
);
const AuctionView = lazy(() =>
  import("@/views/auction/auction-view").then((m) => ({ default: m.AuctionView })),
);

/**
 * Calculate night overlay opacity based on current local time
 * Full daylight (0 opacity): 10:00 - 16:00
 * Night (0.9 opacity): 22:00 - 04:00
 * Gradual transition during dawn/dusk
 */
const WEATHER_TYPES: WeatherType[] = ["none", "clouds", "rain", "snow"];

const getRandomWeather = (): WeatherType => {
  const index = Math.floor(Math.random() * WEATHER_TYPES.length);
  return WEATHER_TYPES[index] ?? "clouds";
};

const getNightOpacity = (): number => {
  const hour = new Date().getHours();

  // Full daylight: 10:00 - 16:00
  if (hour >= 10 && hour < 16) return 0;

  // Full night: 22:00 - 04:00
  if (hour >= 22 || hour < 4) return 0.9;

  // Dawn transition: 04:00 - 10:00 (0.9 -> 0)
  if (hour >= 4 && hour < 10) {
    return 0.9 * (1 - (hour - 4) / 6);
  }

  // Dusk transition: 16:00 - 22:00 (0 -> 0.9)
  return 0.9 * ((hour - 16) / 6);
};

// Home's entrance choreography plays once per session, the first time the
// player lands here from the loading screen. Subsequent returns from a
// sub-view (Shop, Inventory...) get a fast, subtle fade instead so
// navigation stays snappy. This single timing map is the "score" for the
// full sequence (seconds).
let hasPlayedHomeIntro = false;

const HOME_INTRO_TIMING = {
  header: 0,
  campfire: 0.15,
  navStart: 0.45,
  navStagger: 0.07,
  play: 0.95,
} as const;

const routeToView: Record<RouteNames, React.ReactNode> = {
  [RouteNames.PLAY_MENU]: null,
  [RouteNames.DOJO]: <DojoView />,
  [RouteNames.DOJO_TRAINING_CONFIG]: <DojoTrainingConfigView />,
  [RouteNames.DOJO_TEAM_SELECT]: <DojoTeamSelectView />,
  [RouteNames.DOJO_SANDBOX]: <DojoSandboxView />,
  // Dojo battle is rendered inside the Dojo flow views (not a standalone route view)
  [RouteNames.DOJO_BATTLE]: null,
  [RouteNames.INVENTORY_POKE]: null,
  [RouteNames.LEADERBOARD]: null,
  [RouteNames.SHOP]: <ShopView />,
  [RouteNames.ARENA_TEAM_SELECT]: <ArenaTeamSelectView />,
  [RouteNames.DUEL_QUEUE]: <Duel />,
  [RouteNames.POKEMON_INFO]: <PokemonInfoView />,
  [RouteNames.BATTLE_TEST]: <Battle />,
  [RouteNames.PLAYER_PROFILE]: null,
  [RouteNames.PROFILE_SETTINGS]: <ProfileSettingsView />,
  // Dex routes (content-collections-dex)
  [RouteNames.DEX]: <DexHomeView />,
  [RouteNames.DEX_DETAILS]: <DexSetDetailsView />,
  // Item deck routes
  [RouteNames.ITEM_DECK_LIST]: null,
  [RouteNames.ITEM_DECK_EDIT]: null,
  [RouteNames.TRAINING_FACILITY]: <TrainingFacilityView />,
  // Pack route
  [RouteNames.PACKS]: <PacksView />,
  // Siege routes (core-siege) — the SiegeView owns its internal lobby/battle/summary flow
  [RouteNames.SIEGE]: <SiegeView />,
  // Challenge routes (content-challenges)
  [RouteNames.CHALLENGE]: <ChallengeView />,
  // Auction route (econ-auction)
  [RouteNames.AUCTION]: <AuctionView />,
  // Social layer (social-layer, P2)
  [RouteNames.FRIENDS]: null,
};

export default function Home() {
  const {
    currentRoute,
    setCurrentRoute,
    setSelectedPokemons,
    setIntent,
    setViewedPlayerId,
    popBackAction,
    suppressBackButton,
  } = useSPAContext();
  const { getAccessToken } = useAuth();
  const {
    pokeCoins,
    pokeShards,
    playerCountry,
    username,
    mainDeckPokemons,
    duelStatus: initialDuelStatus,
    state: userState,
  } = useUser();
  // Snapshot once: true only for the very first Home render this session.
  const playIntro = !hasPlayedHomeIntro;
  useEffect(() => {
    hasPlayedHomeIntro = true;
  }, []);

  // Random weather on mount to show off the effects
  const [weather] = useState<WeatherType>(getRandomWeather);
  const [nightOpacity, setNightOpacity] = useState(getNightOpacity);

  // Recompute the night overlay periodically so it stays accurate through dawn/dusk
  useEffect(() => {
    const interval = setInterval(() => setNightOpacity(getNightOpacity()), 60000);
    return () => clearInterval(interval);
  }, []);

  const loading = userState.status === "loading";

  // Use hooks to get duel status for the PLAY button indicator
  const isOnHomeScreen = !currentRoute;
  const { queueStatus, currentDuelId } = useDuelQueue(
    isOnHomeScreen,
    initialDuelStatus,
  );

  const isPlayMenuOpen = currentRoute === RouteNames.PLAY_MENU;
  const [renderPlayMenu, setRenderPlayMenu] = useState(false);

  const isInventoryPokeOpen = currentRoute === RouteNames.INVENTORY_POKE;
  const [renderInventoryPoke, setRenderInventoryPoke] = useState(false);

  const isLeaderboardOpen = currentRoute === RouteNames.LEADERBOARD;
  const [renderLeaderboard, setRenderLeaderboard] = useState(false);

  const isFriendsOpen = currentRoute === RouteNames.FRIENDS;
  const [renderFriends, setRenderFriends] = useState(false);

  const isDeckOpen =
    currentRoute === RouteNames.ITEM_DECK_LIST ||
    currentRoute === RouteNames.ITEM_DECK_EDIT;
  const [renderDeck, setRenderDeck] = useState(false);

  const isProfileOpen = currentRoute === RouteNames.PLAYER_PROFILE;
  const [renderProfile, setRenderProfile] = useState(false);


  useEffect(() => {
    if (!currentRoute) {
      setSelectedPokemons([]);
    }
  }, [currentRoute, setSelectedPokemons]);

  useEffect(() => {
    if (isPlayMenuOpen) {
      setRenderPlayMenu(true);
    } else {
      if (renderPlayMenu) {
        const timer = setTimeout(() => setRenderPlayMenu(false), 300);
        return () => clearTimeout(timer);
      }
    }
  }, [isPlayMenuOpen, renderPlayMenu]);

  useEffect(() => {
    if (isInventoryPokeOpen) {
      setRenderInventoryPoke(true);
    } else {
      if (renderInventoryPoke) {
        const timer = setTimeout(() => setRenderInventoryPoke(false), 500); // 500ms for slide animation
        return () => clearTimeout(timer);
      }
    }
  }, [isInventoryPokeOpen, renderInventoryPoke]);

  useEffect(() => {
    if (isLeaderboardOpen) {
      setRenderLeaderboard(true);
    } else {
      if (renderLeaderboard) {
        const timer = setTimeout(() => setRenderLeaderboard(false), 500); // 500ms for slide animation
        return () => clearTimeout(timer);
      }
    }
  }, [isLeaderboardOpen, renderLeaderboard]);

  useEffect(() => {
    if (isFriendsOpen) {
      setRenderFriends(true);
    } else {
      if (renderFriends) {
        const timer = setTimeout(() => setRenderFriends(false), 500);
        return () => clearTimeout(timer);
      }
    }
  }, [isFriendsOpen, renderFriends]);

  useEffect(() => {
    if (isDeckOpen) {
      setRenderDeck(true);
    } else {
      if (renderDeck) {
        const timer = setTimeout(() => setRenderDeck(false), 500);
        return () => clearTimeout(timer);
      }
    }
  }, [isDeckOpen, renderDeck]);

  useEffect(() => {
    if (isProfileOpen) {
      setRenderProfile(true);
    } else {
      if (renderProfile) {
        const timer = setTimeout(() => setRenderProfile(false), 500);
        return () => clearTimeout(timer);
      }
    }
  }, [isProfileOpen, renderProfile]);

  // Check for local test mode on mount (dev builds only)
  useEffect(() => {
    if (!import.meta.env.DEV) return;

    const urlParams = new URLSearchParams(window.location.search);
    const localTest = urlParams.get("localTest") === "true";

    if (localTest) {
      console.log("[Home] Local test mode detected, navigating to BATTLE_TEST");
      setCurrentRoute(RouteNames.BATTLE_TEST);
      return;
    }

    const routeParam = urlParams.get("route");
    if (
      routeParam &&
      (Object.values(RouteNames) as string[]).includes(routeParam)
    ) {
      setCurrentRoute(routeParam as RouteNames);
    }
  }, [setCurrentRoute]);

  // Determine if there's an active game (duel)
  const isDuelActive = queueStatus === "IN_DUEL" || queueStatus === "MATCHED";
  const isDuelWaiting = queueStatus === "WAITING";
  const hasActiveGame = isDuelActive;

  /**
   * Handle play button click - go directly to the relevant view
   */
  const handlePlayClick = () => {
    // If duel is active or waiting, go to duel queue (reconnect)
    if (isDuelActive || isDuelWaiting) {
      setCurrentRoute(RouteNames.DUEL_QUEUE);
      return;
    }

    // No active game, go to play menu
    setCurrentRoute(RouteNames.PLAY_MENU);
  };

  const handleBack = useCallback(() => {
    const handled = popBackAction();
    if (!handled) {
      setCurrentRoute(undefined);
    }
  }, [popBackAction, setCurrentRoute]);

  // Dev-only tools live under /dev/* (gated by import.meta.env.DEV inside the
  // resolver, tree-shaken out of prod). When on a dev route we short-circuit
  // the SPA shell entirely (see the early return below).
  const pathname = window.location.pathname;
  const devView = resolveDevView(pathname);
  const devRoute = isDevRoute(pathname);
  const currentView = currentRoute ? routeToView[currentRoute] : null;

  // Footer back button is shown whenever there's somewhere to go back to.
  const showBackButton = Boolean(
      currentView ||
        renderPlayMenu ||
        renderInventoryPoke ||
        renderLeaderboard ||
        renderFriends ||
        renderDeck ||
        renderProfile,
    ) &&
    currentRoute !== RouteNames.DUEL_QUEUE &&
    !suppressBackButton;

  const handleCampfirePokemonClick = async (pokemon: any) => {
    if (!getAccessToken()) return;

    // Show loading state if we want, but let's just fetch
    try {
      const response = await listInventoryPokemons();
      if (response.ok && response.data) {
        const fullPokemon = response.data.player_pokemons.find(
          (p) => p.id === pokemon.id,
        );
        if (fullPokemon) {
          setSelectedPokemons([fullPokemon]);
          setCurrentRoute(RouteNames.POKEMON_INFO);
        }
      }
    } catch (error) {
      console.error("Failed to fetch full pokemon data:", error);
      // Fallback to partial data if fetch fails
      setSelectedPokemons([pokemon]);
      setCurrentRoute(RouteNames.POKEMON_INFO);
    }
  };

  // Dev tools render full-screen and own their own chrome; bypass the SPA shell.
  if (devRoute) {
    return (
      <ErrorBoundary onReset={() => (window.location.pathname = "/")}>
        {devView}
      </ErrorBoundary>
    );
  }

  return (
    <main
      className={classNames(
        "w-full h-full bg-[#5CD599] relative z-1  overflow-hidden flex items-center justify-start flex-col",
        "max-w-[480px] ",
      )}
      style={{
        backgroundImage: "url(/assets/app-ui/backgrounds/sky.jpg)",
        backgroundSize: "cover",
        backgroundPosition: "bottom",
        backgroundRepeat: "no-repeat",
        animation: "scrollBackground 240s linear infinite",
        height: "min(100svh, calc(480px * 18 / 9))",
      }}
      id="app-container"
    >
      <style
        dangerouslySetInnerHTML={{
          __html: `
          @keyframes scrollBackground {
            0% {
              background-position: 0% 50%;
            }
            100% {
              background-position: 100% 50%;
            }
          }
        `,
        }}
      />
      <div
        className="flex flex-col justify-start items-center w-full h-full"
        style={{
          backgroundImage: "url(/assets/app-ui/backgrounds/test.png)",
          backgroundSize: "105%",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      >
        {currentView ? (
          <ErrorBoundary
            key={currentRoute}
            onReset={() => setCurrentRoute(undefined)}
          >
            <Suspense fallback={<RouteLoadingFallback />}>
              {currentView}
            </Suspense>
          </ErrorBoundary>
        ) : (
          <>
            <EnvironmentalEffects weather={weather} />
            <NightOverlay opacity={nightOpacity} />
            {/* Top Bar */}
            <MotionItem
              variant={playIntro ? "fadeDown" : "fadeFast"}
              delay={playIntro ? HOME_INTRO_TIMING.header : undefined}
              className="w-full relative z-30"
            >
              <div
                className={classNames("w-full ", {
                  "animate-slide-up-exit":
                    isInventoryPokeOpen ||
                    isLeaderboardOpen ||
                    isPlayMenuOpen ||
                    isFriendsOpen ||
                    isDeckOpen ||
                    isProfileOpen,
                  "animate-slide-down":
                    (!isInventoryPokeOpen && renderInventoryPoke) ||
                    (!isLeaderboardOpen && renderLeaderboard) ||
                    (!isPlayMenuOpen && renderPlayMenu) ||
                    (!isFriendsOpen && renderFriends) ||
                    (!isDeckOpen && renderDeck) ||
                    (!isProfileOpen && renderProfile),
                })}
              >
                <HomeHeader />
              </div>
            </MotionItem>

            {/* Main Content Area */}
            <div className="relative flex flex-1 justify-center items-center p-4 w-full">
              {/* Center Content */}
              <div className="z-10 flex flex-col items-center gap-4 w-full">
                {loading ? (
                  <div className="flex justify-center items-center w-48 h-48">
                    <div className="border-4 border-pk-blue-4 border-t-transparent rounded-full w-12 h-12 animate-spin" />
                  </div>
                ) : (
                  <>
                    {/* AnimatedVFX Examples */}
                    <div className="top-32 right-16 z-50 absolute">
                      <AnimatedVFX
                        id="healing-1-green"
                        spriteUrl="/assets/spells/self/healing-1-green.png"
                        spriteScale={0.3}
                        spriteAlpha={1}
                        position={{ x: 0, y: 0 }}
                        loops={1}
                        speed={1.5}
                        ping_pong={false}
                      />
                    </div>

                    {/* Pokemon Team Display */}
                    <MotionItem
                      variant={playIntro ? "fadeUp" : "fadeFast"}
                      delay={playIntro ? HOME_INTRO_TIMING.campfire : undefined}
                      className="w-full"
                    >
                      <PokemonCampfireDisplay
                        pokemons={mainDeckPokemons}
                        onPokemonClick={handleCampfirePokemonClick}
                        className="w-full translate-y-[120px] xs:translate-y-[160px]"
                      />
                    </MotionItem>
                  </>
                )}
              </div>
            </div>

            {/* Play Button */}
            <div className="z-40 flex flex-col items-end pb-1 w-full gap-2">
              <div className="w-full flex justify-center px-4">
                <HomeFeedTicker />
              </div>
              <MotionItem
                variant={playIntro ? "heroPop" : "fadeFast"}
                delay={playIntro ? HOME_INTRO_TIMING.play : undefined}
              >
                <Button
                  onClick={handlePlayClick}
                  variant={
                    isDuelWaiting ? "yellow" : hasActiveGame ? "red" : "gray"
                  }
                >
                  <div className="flex items-center gap-3 px-12 py-2">
                    <span className="drop-shadow-lg text-2xl xs:text-3xl filter">
                      {hasActiveGame ? "🔌" : ""}
                    </span>
                    <div className="flex flex-col items-start">
                      <span className="drop-shadow-[2px_2px_0_rgba(0,0,0,0.5)] font-black text-white text-xl xs:text-2xl tracking-wider">
                        {isDuelWaiting
                          ? "IN QUEUE..."
                          : hasActiveGame
                          ? "RECONNECT"
                          : "PLAY"}
                      </span>
                      <span
                        className={`text-xs xs:text-sm font-bold tracking-[0.2em] uppercase drop-shadow-sm -mt-1 ${
                          isDuelWaiting
                            ? "text-yellow-400 animate-pulse"
                            : isDuelActive
                            ? "text-red-300 animate-pulse"
                            : "text-pk-yellow-1"
                        }`}
                      >
                        {isDuelWaiting
                          ? "Waiting..."
                          : isDuelActive
                          ? "DUEL ACTIVE"
                          : ""}
                      </span>
                    </div>
                  </div>
                </Button>
              </MotionItem>
            </div>

            {/* Bottom Navigation */}
            <HomeBottomNav
              playIntro={playIntro}
              navStart={HOME_INTRO_TIMING.navStart}
              navStagger={HOME_INTRO_TIMING.navStagger}
            />

            {renderPlayMenu && (
              <div
                className={classNames("absolute inset-0 z-50", {
                  "animate-slide-down": isPlayMenuOpen,
                  "animate-slide-up-exit": !isPlayMenuOpen,
                })}
              >
                <PlayMenuView />
              </div>
            )}

            {renderInventoryPoke && (
              <div
                className={classNames("absolute inset-0 z-50", {
                  "animate-slide-down": isInventoryPokeOpen,
                  "animate-slide-up-exit": !isInventoryPokeOpen,
                })}
              >
                <InventoryPokeView />
              </div>
            )}

            {renderLeaderboard && (
              <div
                className={classNames("absolute inset-0 z-50", {
                  "animate-slide-down": isLeaderboardOpen,
                  "animate-slide-up-exit": !isLeaderboardOpen,
                })}
              >
                <LeaderboardView />
              </div>
            )}

            {renderFriends && (
              <div
                className={classNames("absolute inset-0 z-50", {
                  "animate-slide-down": isFriendsOpen,
                  "animate-slide-up-exit": !isFriendsOpen,
                })}
              >
                <FriendsView />
              </div>
            )}

            {renderDeck && (
              <div
                className={classNames("absolute inset-0 z-50", {
                  "animate-slide-down": isDeckOpen,
                  "animate-slide-up-exit": !isDeckOpen,
                })}
              >
                {currentRoute === RouteNames.ITEM_DECK_EDIT ? (
                  <DeckEditView />
                ) : (
                  <DeckListView />
                )}
              </div>
            )}

            {renderProfile && (
              <>
                <div
                  className={classNames("absolute inset-0 z-50 bg-black/60", {
                    "animate-fade-in": isProfileOpen,
                    "animate-fade-out": !isProfileOpen,
                  })}
                />
                <div
                  className={classNames("absolute inset-0 z-50", {
                    "animate-slide-down": isProfileOpen,
                    "animate-slide-up-exit": !isProfileOpen,
                  })}
                >
                  <PlayerProfileView />
                </div>
              </>
            )}

          </>
        )}

        {/* Footer with Share and Back buttons (only shown when in a view) */}
        {showBackButton && (
          <footer className="right-0 bottom-0 left-0 z-50 absolute flex justify-start items-center w-full pointer-events-none">
            <div className="translate-x-2 translate-y-2 pointer-events-auto">
              <Button variant="default" onClick={handleBack}>
                <BackIcon width={48} height={48} />
              </Button>
            </div>
          </footer>
        )}
      </div>
    </main>
  );
}

